import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import MemberProfile from '@/pages/MemberProfile';
import TrainerProfile from '@/pages/TrainerProfile';

const UserProfileRouter: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserRole = async () => {
      if (!id) {
        setError('User ID is required');
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('users')
          .select('role')
          .eq('auth_id', id)
          .single();

        if (error) throw error;
        
        setUserRole(data?.role || 'member');
      } catch (err) {
        console.error('Error fetching user role:', err);
        setError('Failed to load user profile');
        // Default to member profile on error
        setUserRole('member');
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserRole();
  }, [id]);

  if (isLoading) {
    return <div className="flex justify-center items-center min-h-[60vh]">
      <div className="h-12 w-12 border-4 border-gold-400 border-t-transparent rounded-full animate-spin"></div>
    </div>;
  }

  if (error) {
    return <div className="text-center py-12">
      <h2 className="text-2xl font-bold text-white mb-4">Error Loading Profile</h2>
      <p className="text-dark-800 mb-6">{error}</p>
      <button 
        onClick={() => navigate('/dashboard')}
        className="px-4 py-2 bg-gold-400 hover:bg-gold-500 text-dark-50 rounded-lg transition-colors"
      >
        Back to Dashboard
      </button>
    </div>;
  }

  // Render the appropriate profile component based on user role
  return userRole === 'trainer' ? <TrainerProfile /> : <MemberProfile />;
};

export default UserProfileRouter;